/**
 * WebSocket Service for Real-Time Updates
 * Handles connection management, reconnection, and event dispatching
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.subscribers = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.isConnecting = false;
    this.messageQueue = [];
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    const wsUrl = this.getWebSocketUrl();
    
    try {
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('[WS] Connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.emit('connection', { status: 'connected' });
        
        // Send queued messages
        while (this.messageQueue.length > 0) {
          const msg = this.messageQueue.shift();
          this.send(msg.type, msg.data);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (e) {
          console.error('[WS] Failed to parse message:', e);
        }
      };

      this.ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        this.isConnecting = false;
        this.emit('connection', { status: 'disconnected' });
        
        // Attempt reconnection if not intentionally closed
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WS] Error:', error);
        this.isConnecting = false;
        this.emit('error', { error });
      };
    } catch (e) {
      console.error('[WS] Connection failed:', e);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  /**
   * Get WebSocket URL from environment
   */
  getWebSocketUrl() {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
    // No explicit backend URL: we're served behind a reverse proxy on the
    // same origin (e.g. nginx on the Pi), so derive host/protocol from the page.
    if (!backendUrl) {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      return `${wsProtocol}://${window.location.host}/ws`;
    }
    // Convert http(s) to ws(s)
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}://${wsHost}/ws`;
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnect() {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Handle incoming messages and dispatch to subscribers
   */
  handleMessage(message) {
    const { type, data } = message;
    this.emit(type, data);
  }

  /**
   * Send message to server
   */
  send(type, data = {}) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, data }));
    } else {
      // Queue message for when connection is established
      this.messageQueue.push({ type, data });
      
      // Try to connect if not already
      if (!this.isConnecting) {
        this.connect();
      }
    }
  }

  /**
   * Subscribe to a message type
   */
  subscribe(type, callback) {
    if (!this.subscribers.has(type)) {
      this.subscribers.set(type, new Set());
    }
    this.subscribers.get(type).add(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.get(type)?.delete(callback);
    };
  }

  /**
   * Emit event to all subscribers
   */
  emit(type, data) {
    const callbacks = this.subscribers.get(type);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (e) {
          console.error(`[WS] Error in subscriber for ${type}:`, e);
        }
      });
    }

    // Also emit to wildcard subscribers
    const wildcardCallbacks = this.subscribers.get('*');
    if (wildcardCallbacks) {
      wildcardCallbacks.forEach(callback => {
        try {
          callback({ type, data });
        } catch (e) {
          console.error('[WS] Error in wildcard subscriber:', e);
        }
      });
    }
  }

  /**
   * Disconnect from server
   */
  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }
  }

  /**
   * Check connection status
   */
  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Singleton instance
const wsService = new WebSocketService();

export default wsService;

// Event type constants
export const WS_EVENTS = {
  // Orders
  ORDER_CREATED: 'order_created',
  ORDER_UPDATED: 'order_updated',
  ORDER_STATUS_CHANGED: 'order_status_changed',
  
  // Tables
  TABLE_STATUS_CHANGED: 'table_status_changed',
  TABLE_OPENED: 'table_opened',
  TABLE_CLOSED: 'table_closed',
  
  // Kitchen
  TICKET_UPDATED: 'ticket_updated',   // a ticket's Start/Ready/Done moved (backend routes/kitchen.py)
  KITCHEN_ORDER_NEW: 'kitchen_order_new',
  KITCHEN_ORDER_READY: 'kitchen_order_ready',
  
  // Connection
  CONNECTION: 'connection',
  ERROR: 'error',
};
